import React, { useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useSearchParams } from 'react-router-dom';
import CallDetails from '../callHistory/callDetails';
import CallTable from '../callHistory/callLogs';
import ChatLogs from '../chatHistory/chatLogs';
import ChatDetails from '../chatHistory/chatDetails';
import OrdersTable from '../orders/OrdersTable';
import OrderDetails from '../orders/OrderDetails';
import TicketCard from '../tickets/ticketCard';
import { getOrderStatus, getEquipmentList, getTotalAmount } from '../../utils/orderUtils';
import { useCustomer } from '../../contexts/CustomerContext';

const BestMatchesTab = ({ 
  bestMatches, 
  selectedCall, 
  setSelectedCall 
}) => {
  const [searchParams] = useSearchParams();
  const [selectedRows, setSelectedRows] = useState([]);
  const { getEffectiveCustomerId } = useCustomer();
  const customerId = getEffectiveCustomerId();
  const isKronk = customerId === 'kronk';
  const isWards = customerId === 'wards';

  if (bestMatches.length === 0) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <div className="text-center">
          <Icon icon="material-symbols:search-off" className="text-6xl text-bgAccent mb-4" />
          <p className="text-font-secondary text-lg">No results found</p>
        </div>
      </div>
    );
  }

  // Separate tickets, calls/chat, and orders for different display
  const tickets = bestMatches.filter(item => item.ticketNumber);
  
  // Separate Shopify orders (kronk) from call-based orders (wards) and calls/chat
  const shopifyOrders = bestMatches.filter(item => item.order_number || item.line_items);
  const callsAndWardsOrders = bestMatches.filter(item => !item.ticketNumber && !item.order_number && !item.line_items);
  
  // Further separate calls from wards orders
  const calls = callsAndWardsOrders.filter(item => !item.booking_info || 
    !(item.booking_info.equipment_requested?.length > 0 || 
      item.booking_info.customer_info || 
      item.booking_info.delivery_info));
  const wardsOrders = callsAndWardsOrders.filter(item => item.booking_info && 
    (item.booking_info.equipment_requested?.length > 0 || 
     item.booking_info.customer_info || 
     item.booking_info.delivery_info));
  
  // Combine all orders (Shopify for kronk, booking orders for wards)
  const orders = isKronk ? shopifyOrders : wardsOrders;

  return (
    <div className="flex flex-1 gap-2 w-full min-h-0 overflow-hidden">
      <div className={`
        flex flex-col w-full gap-4 min-w-0 min-h-0 overflow-hidden transition-all duration-300 ease-out
        ${selectedCall ? 'hidden 2xl:flex 2xl:w-1/2' : 'flex'}
      `}>
        {/* Tickets Section */}
        {tickets.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center justify-between gap-2">
              <h3 className="text-lg font-semibold text-font-primary flex items-center gap-2">
                <Icon icon="f7:tickets" className="text-2xl text-accent" />
                Tickets
              </h3>
              <span className="text-sm text-font-secondary">{tickets.length} displayed</span>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {tickets.map((ticket) => (
                <TicketCard
                  key={ticket.id}
                  ticket={ticket}
                />
              ))}
            </div>
          </div>
        )}

        {/* Calls/Chat History Section */}
        {calls.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center justify-between gap-2">
              <h3 className="text-lg font-semibold text-font-primary flex items-center gap-2">
                <Icon icon={isKronk ? "ph:chats-fill" : "material-symbols:call-outline"} className="text-2xl text-accent" />
                {isKronk ? 'Chat History' : 'Calls'}
              </h3>
              <span className="text-sm text-font-secondary">{calls.length} displayed</span>
            </div>
            <div className="w-full h-full flex flex-col">
              {isKronk ? (
                <ChatLogs
                  onRowClick={setSelectedCall}
                  isShort={!!selectedCall}
                  chatRow={selectedCall?.id}
                  displayData={calls}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  className="w-full h-full flex-1 min-h-0"
                />
              ) : (
                // CallTable automatically adapts columns based on client:
                // - celeste/helios: Shows 'Call Id' column
                // - Other clients: Shows 'Equipment' column
                <CallTable
                  onRowClick={setSelectedCall}
                  isShort={false}
                  callRow={selectedCall?.metadata?.stream_sid}
                  displayData={calls}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  className="w-full h-full flex-1 min-h-0"
                />
              )}
            </div>
          </div>
        )}

        {/* Orders Section - Show for Wards and Kronk */}
        {(isWards || isKronk) && orders.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center justify-between gap-2">
              <h3 className="text-lg font-semibold text-font-primary flex items-center gap-2">
                <Icon icon="material-symbols:order-approve-outline" className="text-2xl text-accent" />
                Orders
              </h3>
              <span className="text-sm text-font-secondary">{orders.length} displayed</span>
            </div>
            <div className="w-full h-full flex flex-col">
              <OrdersTable
                paginatedOrders={orders}
                selectedOrder={selectedCall}
                searchParams={searchParams}
                setSelectedOrder={setSelectedCall}
                rowRef={null}
                getEquipmentList={getEquipmentList}
                getOrderStatus={getOrderStatus}
                getTotalAmount={getTotalAmount}
              />
            </div>
          </div>
        )}
      </div>
      {/* Details Panel */}
      {selectedCall && (
        <div className="w-full flex flex-col 2xl:w-1/2 flex-shrink-0 min-h-0 max-h-screen overflow-hidden">
          {/* Determine if it's a call, chat, or order based on structure */}
          {(() => {
            // Check if it's a Shopify order (kronk)
            const isShopifyOrder = selectedCall.order_number || selectedCall.line_items;
            // Check if it's a Wards order (booking order)
            const isWardsOrder = selectedCall.booking_info && 
           (selectedCall.booking_info.equipment_requested?.length > 0 || 
            selectedCall.booking_info.customer_info || 
               selectedCall.booking_info.delivery_info);
            
            if (isShopifyOrder || (isWards && isWardsOrder)) {
              // Show OrderDetails for both Shopify orders (kronk) and booking orders (wards)
              return (
            <OrderDetails 
              selectedOrder={selectedCall}
              searchParams={searchParams}
              setSelectedOrder={setSelectedCall}
              getOrderStatus={getOrderStatus}
              getOrderStatusColor={() => {}} // This will be handled by OrderDetails internally
              handleMarkCompleted={() => {}} // This will be handled by OrderDetails internally
            />
              );
            } else if (isKronk) {
              // For kronk, show ChatDetails for chat items
              return <ChatDetails chat={selectedCall} onClose={() => setSelectedCall(null)} />;
            } else {
              // For other clients, show CallDetails for calls
            // CallDetails automatically adapts tabs based on client:
            // - celeste/helios: Shows 3 tabs (Transcripts, Analysis, Tickets - no Order Details)
            // - Other clients: Shows 4 tabs (includes Order Details)
              return <CallDetails call={selectedCall} onClose={() => setSelectedCall(null)} />;
            }
          })()}
        </div>
      )}
    </div>
  );
};

export default BestMatchesTab;
